"use client"

import { useContext, useEffect, useMemo, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import EditIcon from "@mui/icons-material/Edit"
import AutorenewIcon from "@mui/icons-material/Autorenew"
import NoteAddIcon from "@mui/icons-material/NoteAdd"
import { ThemeProvider } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import CustomPagination from "../transaccionComponent/CustomPagination"
import ModalRrhhLiquidacion from "./ModalRrhhLiquidacion.js"
import ModalRrhhNovedadLiquidacion from "./ModalRrhhNovedadLiquidacion.js"
import {
  RRHH_ESTADOS_LIQUIDACION,
  extractErrorMessage,
  formatCurrency,
  formatFecha,
  formatPeriodoMensual,
  formatDuracionMinutos,
  getEmpleadoNombreCompleto,
  getEstadoLiquidacionMeta,
  rrhhTheme,
} from "./rrhhUi.js"

const PAGE_SIZE = 10

const getCurrentPeriod = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
}

const getTipoConceptoChipStyles = (tipoConcepto) => {
  switch (String(tipoConcepto || "").toUpperCase()) {
    case "HABER":
      return { color: "#166534", backgroundColor: alpha("#22c55e", 0.14) }
    case "DESCUENTO":
      return { color: "#b91c1c", backgroundColor: alpha("#ef4444", 0.14) }
    default:
      return { color: "#475569", backgroundColor: alpha("#64748b", 0.14) }
  }
}

const TablaRrhhLiquidacionesComponent = () => {
  const {
    getRrhhEmpleados,
    getRrhhLiquidaciones,
    getRrhhLiquidacionById,
    postRrhhLiquidacion,
    updateRrhhLiquidacion,
    postRrhhRecalcularLiquidacion,
    getRrhhNovedadesLiquidacion,
    postRrhhNovedadLiquidacion,
    updateRrhhNovedadLiquidacion,
  } = useContext(ErpContext)

  const [rows, setRows] = useState([])
  const [novedadesRows, setNovedadesRows] = useState([])
  const [empleadosOptions, setEmpleadosOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingNovedades, setLoadingNovedades] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const [searchText, setSearchText] = useState("")
  const [periodoFilter, setPeriodoFilter] = useState(getCurrentPeriod())
  const [estadoFilter, setEstadoFilter] = useState(null)

  const [liquidacionModalOpen, setLiquidacionModalOpen] = useState(false)
  const [liquidacionModalMode, setLiquidacionModalMode] = useState("create")
  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null)

  const [novedadModalOpen, setNovedadModalOpen] = useState(false)
  const [selectedNovedad, setSelectedNovedad] = useState(null)

  const loadEmpleadosOptions = async () => {
    try {
      const response = await getRrhhEmpleados({ page: 1, limit: 300 })
      setEmpleadosOptions(response?.data || [])
    } catch (loadError) {
      console.error("Error cargando catálogo de empleados RRHH:", loadError)
    }
  }

  const loadLiquidaciones = async () => {
    setLoading(true)
    try {
      const response = await getRrhhLiquidaciones({
        page,
        limit: PAGE_SIZE,
        search: searchText.trim() || undefined,
        periodo: periodoFilter || undefined,
        estado: estadoFilter?.value || undefined,
      })

      setRows(response?.data || [])
      setTotalPages(response?.totalPages || 1)
      setTotalCount(response?.total || 0)
      setError(null)
    } catch (loadError) {
      setError(extractErrorMessage(loadError, "No se pudieron cargar las liquidaciones RRHH."))
    } finally {
      setLoading(false)
    }
  }

  const loadNovedades = async () => {
    setLoadingNovedades(true)
    try {
      const response = await getRrhhNovedadesLiquidacion({
        page: 1,
        limit: 100,
        search: searchText.trim() || undefined,
        periodo: periodoFilter || undefined,
      })

      setNovedadesRows(response?.data || [])
      setError(null)
    } catch (loadError) {
      setError(extractErrorMessage(loadError, "No se pudieron cargar las novedades RRHH."))
    } finally {
      setLoadingNovedades(false)
    }
  }

  useEffect(() => {
    loadEmpleadosOptions()
  }, [])

  useEffect(() => {
    loadLiquidaciones()
  }, [page, searchText, periodoFilter, estadoFilter])

  useEffect(() => {
    loadNovedades()
  }, [searchText, periodoFilter])

  const handleRefresh = async () => {
    setError(null)
    setSuccess(null)
    await Promise.all([loadEmpleadosOptions(), loadLiquidaciones(), loadNovedades()])
  }

  const handleOpenCreateLiquidacion = () => {
    setSelectedLiquidacion(null)
    setLiquidacionModalMode("create")
    setLiquidacionModalOpen(true)
    setError(null)
  }

  const handleOpenDetailLiquidacion = async (idLiquidacion) => {
    setLiquidacionModalMode("edit")
    setLiquidacionModalOpen(true)
    setLoadingDetail(true)
    setError(null)

    try {
      const detail = await getRrhhLiquidacionById(idLiquidacion)
      setSelectedLiquidacion(detail)
    } catch (detailError) {
      setError(extractErrorMessage(detailError, "No se pudo cargar el detalle de la liquidación."))
      setLiquidacionModalOpen(false)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleSaveLiquidacion = async (payload) => {
    setSaving(true)
    try {
      if (liquidacionModalMode === "create") {
        await postRrhhLiquidacion({ liquidacion: payload })
        setSuccess("Liquidación generada correctamente.")
      } else if (selectedLiquidacion?.id) {
        const updated = await updateRrhhLiquidacion(selectedLiquidacion.id, { liquidacion: payload })
        setSelectedLiquidacion(updated)
        setSuccess("Liquidación actualizada correctamente.")
      }

      setLiquidacionModalOpen(false)
      await loadLiquidaciones()
    } catch (saveError) {
      setError(extractErrorMessage(saveError, "No se pudo guardar la liquidación."))
      throw saveError
    } finally {
      setSaving(false)
    }
  }

  const handleRecalculateLiquidacion = async (row) => {
    try {
      await postRrhhRecalcularLiquidacion(row.id)
      setSuccess("Liquidación recalculada correctamente.")
      await Promise.all([loadLiquidaciones(), loadNovedades()])
    } catch (recalcError) {
      setError(extractErrorMessage(recalcError, "No se pudo recalcular la liquidación."))
    }
  }

  const handleOpenCreateNovedad = () => {
    setSelectedNovedad(null)
    setNovedadModalOpen(true)
    setError(null)
  }

  const handleOpenEditNovedad = (novedad) => {
    setSelectedNovedad(novedad)
    setNovedadModalOpen(true)
    setError(null)
  }

  const handleSaveNovedad = async (payload) => {
    setSaving(true)
    try {
      if (selectedNovedad?.id) {
        await updateRrhhNovedadLiquidacion(selectedNovedad.id, { novedad: payload })
        setSuccess("Novedad actualizada. Si ya existe liquidación del período, recalculala para reflejar el cambio.")
      } else {
        await postRrhhNovedadLiquidacion({ novedad: payload })
        setSuccess("Novedad registrada. Si ya existe liquidación del período, recalculala para incluirla.")
      }

      setNovedadModalOpen(false)
      await loadNovedades()
    } catch (saveError) {
      setError(extractErrorMessage(saveError, "No se pudo guardar la novedad."))
      throw saveError
    } finally {
      setSaving(false)
    }
  }

  const totalNetoPagina = useMemo(
    () => rows.reduce((acc, row) => acc + (Number(row.totalNeto) || 0), 0),
    [rows]
  )

  const aprobadasCount = useMemo(
    () => rows.filter((row) => row.estado === "APROBADA").length,
    [rows]
  )

  const pagadasCount = useMemo(
    () => rows.filter((row) => row.estado === "PAGADA").length,
    [rows]
  )

  const novedadesAprobadasCount = useMemo(
    () => novedadesRows.filter((row) => row.aprobado === true).length,
    [novedadesRows]
  )

  const liquidacionesColumns = [
    {
      field: "periodo",
      headerName: "Período",
      minWidth: 110,
      flex: 0.7,
      valueFormatter: (value) => formatPeriodoMensual(value),
    },
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.4,
      valueGetter: (_, row) => getEmpleadoNombreCompleto(row),
    },
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 110,
      flex: 0.75,
      valueGetter: (_, row) => row.entidad?.rrhhEmpleado?.legajo || "-",
    },
    {
      field: "estado",
      headerName: "Estado",
      minWidth: 130,
      flex: 0.75,
      renderCell: ({ value }) => {
        const meta = getEstadoLiquidacionMeta(value)
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.backgroundColor,
              color: meta.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "puesto",
      headerName: "Puesto / categoría",
      minWidth: 180,
      flex: 1,
      valueGetter: (_, row) => row.contratoBase?.puesto || row.contratoBase?.categoria || "-",
    },
    {
      field: "totalHaberes",
      headerName: "Haberes",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "totalDescuentos",
      headerName: "Descuentos",
      minWidth: 125,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "totalNeto",
      headerName: "Neto",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "totalMinutosExtra",
      headerName: "Extra",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
    {
      field: "fechaLiquidacion",
      headerName: "Liquidada",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatFecha(value),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Ver liquidación">
            <IconButton size="small" onClick={() => handleOpenDetailLiquidacion(row.id)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recalcular">
            <span>
              <IconButton
                size="small"
                onClick={() => handleRecalculateLiquidacion(row)}
                disabled={row.estado !== "BORRADOR"}
              >
                <AutorenewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  const novedadesColumns = [
    {
      field: "fechaNovedad",
      headerName: "Fecha",
      minWidth: 110,
      flex: 0.7,
      valueFormatter: (value) => formatFecha(value),
    },
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.3,
      valueGetter: (_, row) => getEmpleadoNombreCompleto(row),
    },
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 110,
      flex: 0.7,
      valueGetter: (_, row) => row.entidad?.rrhhEmpleado?.legajo || "-",
    },
    {
      field: "categoria",
      headerName: "Categoría",
      minWidth: 120,
      flex: 0.8,
    },
    {
      field: "tipoConcepto",
      headerName: "Impacto",
      minWidth: 120,
      flex: 0.8,
      renderCell: ({ value }) => {
        const styles = getTipoConceptoChipStyles(value)
        return (
          <Chip
            label={value || "-"}
            size="small"
            sx={{
              backgroundColor: styles.backgroundColor,
              color: styles.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      minWidth: 220,
      flex: 1.5,
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "aprobado",
      headerName: "Aprobada",
      minWidth: 110,
      flex: 0.7,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Sí" : "No"}
          size="small"
          sx={{
            backgroundColor: value ? alpha("#22c55e", 0.14) : alpha("#64748b", 0.14),
            color: value ? "#166534" : "#475569",
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      filterable: false,
      minWidth: 90,
      renderCell: ({ row }) => (
        <Tooltip title="Editar novedad">
          <IconButton size="small" onClick={() => handleOpenEditNovedad(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Box sx={{ width: "100%", p: { xs: 1, md: 2 } }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          {loadingDetail && <Alert severity="info">Cargando detalle de la liquidación...</Alert>}

          <Paper sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#1f2937" }}>
                    Liquidaciones RRHH
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Liquidación operativa mensual basada en contrato, asistencia cerrada y novedades aprobadas.
                  </Typography>
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="contained" startIcon={<AddCircleIcon />} onClick={handleOpenCreateLiquidacion}>
                    Nueva liquidación
                  </Button>
                  <Button variant="outlined" startIcon={<NoteAddIcon />} onClick={handleOpenCreateNovedad}>
                    Nueva novedad
                  </Button>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                    Actualizar
                  </Button>
                </Stack>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  placeholder="Buscar por empleado, legajo, DNI o período"
                  value={searchText}
                  onChange={(event) => {
                    setSearchText(event.target.value)
                    setPage(1)
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Período"
                  type="month"
                  value={periodoFilter}
                  onChange={(event) => {
                    setPeriodoFilter(event.target.value)
                    setPage(1)
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { md: 180 } }}
                />

                <Autocomplete
                  sx={{ minWidth: { md: 220 } }}
                  options={RRHH_ESTADOS_LIQUIDACION}
                  value={estadoFilter}
                  onChange={(_, value) => {
                    setEstadoFilter(value)
                    setPage(1)
                  }}
                  renderInput={(params) => <TextField {...params} label="Estado" />}
                />
              </Stack>
            </Stack>
          </Paper>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Paper sx={{ p: 2, flex: 1, borderLeft: "4px solid #f59e0b" }}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>Neto página</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(totalNetoPagina)}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, borderLeft: "4px solid #3b82f6" }}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>Aprobadas</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{aprobadasCount}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, borderLeft: "4px solid #22c55e" }}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>Pagadas</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{pagadasCount}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, borderLeft: "4px solid #64748b" }}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>Novedades aprobadas</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{novedadesAprobadasCount}</Typography>
            </Paper>
          </Stack>

          <Paper sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Liquidaciones del período
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Total registros: {totalCount}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ width: "100%" }}>
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={liquidacionesColumns}
                  loading={loading}
                  getRowHeight={() => 56}
                  disableRowSelectionOnClick
                  hideFooter
                  sx={{ minHeight: 520 }}
                />
              </Box>

              <CustomPagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Novedades del período
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Las novedades aprobadas impactan al recalcular la liquidación correspondiente.
                </Typography>
              </Box>

              <Box sx={{ width: "100%" }}>
                <DataGrid
                  autoHeight
                  rows={novedadesRows}
                  columns={novedadesColumns}
                  loading={loadingNovedades}
                  getRowHeight={() => 56}
                  disableRowSelectionOnClick
                  hideFooter
                  sx={{ minHeight: 320 }}
                />
              </Box>
            </Stack>
          </Paper>
        </Stack>

        <ModalRrhhLiquidacion
          open={liquidacionModalOpen}
          onClose={() => {
            if (saving) return
            setLiquidacionModalOpen(false)
          }}
          onSubmit={handleSaveLiquidacion}
          liquidacion={selectedLiquidacion}
          empleados={empleadosOptions}
          mode={liquidacionModalMode}
          submitting={saving}
        />

        <ModalRrhhNovedadLiquidacion
          open={novedadModalOpen}
          onClose={() => {
            if (saving) return
            setNovedadModalOpen(false)
          }}
          onSubmit={handleSaveNovedad}
          novedad={selectedNovedad}
          empleados={empleadosOptions}
          submitting={saving}
        />
      </Box>
    </ThemeProvider>
  )
}

export default TablaRrhhLiquidacionesComponent
